import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './DispatchCall.css';

const COST_PER_CALL = 30;

// IMS API 에러 코드 매핑
const IMS_ERROR_CODES: { [key: string]: string } = {
  '0x0000': '성공',
  '0x0800': '기타 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
  '0x0804': '이미 서비스가 진행중입니다. 잠시 후 다시 시도해주세요.',
  '0x0814': '호스트 서버에 연결되어 있지 않습니다. 관리자에게 문의해주세요.',
  '0x0818': '잘못된 전화번호 형식입니다. 전화번호를 확인해주세요.',
  '0x8003': '메시지 형식에 오류가 있습니다. 메시지 내용을 확인해주세요.',
  '0x8074': '메시지 길이가 너무 깁니다. 메시지를 짧게 수정해주세요.',
  '0x080B': '사용자가 로그인되어 있지 않습니다. 관리자에게 문의해주세요.',
  '0x8075': '부가서비스에 가입되어 있지 않습니다. 관리자에게 문의해주세요.'
};

// 에러 메시지에서 에러 코드 추출 및 변환
const getErrorMessage = (errorMsg: string): string => {
  if (!errorMsg) return '알 수 없는 오류가 발생했습니다.';

  // 에러 코드 패턴 찾기 (0x0000 형식 또는 Result code: 숫자 (0x0000) 형식)
  const errorCodeMatch = errorMsg.match(/0x[0-9A-Fa-f]{4}/i);
  if (errorCodeMatch) {
    const errorCode = errorCodeMatch[0].toLowerCase();
    const friendlyMessage = IMS_ERROR_CODES[errorCode];
    if (friendlyMessage) {
      return friendlyMessage;
    }
  }

  // 에러 코드가 없거나 매핑되지 않은 경우 원본 메시지 반환
  return errorMsg;
};

interface DispatchTemplate {
  id: number;
  name: string;
  message: string;
  question: string;
  choices: Array<{
    key: string;
    label: string;
    status: string;
  }>;
}

interface DispatchAlert {
  callItemId: number;
  phoneNumber: string;
  responseKey: string;
  responseLabel: string;
  status: string;
  respondedAt: string;
  scheduledTime?: string;
}

interface DispatchHistory {
  id: string;
  phoneNumbers: string;
  timestamp: string;
  count: number;
  templateName: string;
}

interface MessageTemplate {
  id: string;
  message: string;
  question: string;
  choices?: Array<{ key: string; label: string; status: string }>;
  timestamp: string;
}

interface DispatchResult {
  id: string;
  phoneNumber: string;
  status: 'pending' | 'calling' | 'success' | 'failed';
  templateName: string;
  timestamp: string;
}

interface FullDispatchHistory {
  id: number;
  companyId: number;
  phoneNumber: string;
  message: string;
  status: 'success' | 'failed' | 'pending';
  serviceType: string;
  sentAt: string;
  cost: number;
  errorMessage?: string;
  surveyTitle?: string;
  surveyAnswers?: Array<{
    questionId: number;
    questionText: string;
    answer: string;
    recordedAt: string;
  }>;
  eventCount: number;
}

const DispatchCall: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'send' | 'report'>('send');
  const [templates, setTemplates] = useState<DispatchTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState('');
  const [scheduledTimes, setScheduledTimes] = useState('');
  const [useScheduling, setUseScheduling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState<DispatchAlert[]>([]);
  const [reportData, setReportData] = useState<any>(null);

  // 파일 업로드 관련
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [fileRows, setFileRows] = useState<string[][]>([]);
  const [selectedColumn, setSelectedColumn] = useState<number>(-1);
  const [selectedTimeColumn, setSelectedTimeColumn] = useState<number>(-1);

  // 발송 내역 관련
  const [dispatchHistory, setDispatchHistory] = useState<DispatchHistory[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<string[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // 실시간 발송 현황
  const [dispatchResults, setDispatchResults] = useState<DispatchResult[]>([]);

  // 전체 발신 내역 관련
  const [showFullHistoryModal, setShowFullHistoryModal] = useState(false);
  const [fullDispatchHistory, setFullDispatchHistory] = useState<FullDispatchHistory[]>([]);
  const [loadingFullHistory, setLoadingFullHistory] = useState(false);

  // 응답 번호 -> 라벨 매핑을 위한 템플릿 선택지 저장
  const [choicesMap, setChoicesMap] = useState<Map<string, string>>(new Map());

  // 필터 상태
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPhoneNumber, setFilterPhoneNumber] = useState('');
  const [filterServiceType, setFilterServiceType] = useState('TTS_RS');

  // 메시지 템플릿 관련
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [customQuestion, setCustomQuestion] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL ||
    (window.location.hostname === 'localhost' ? 'http://localhost:5111' :
     window.location.hostname === 'autocall.seoul-soft.com' ? 'https://autocall.seoul-soft.com' :
     `http://${window.location.hostname}:5111`);

  useEffect(() => {
    loadTemplates();
    loadDispatchHistory();
    loadMessageTemplates();
  }, []);

  useEffect(() => {
    // 템플릿 선택 시 메시지와 질문 자동 설정
    if (selectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        setCustomMessage(template.message);
        setCustomQuestion(template.question);
      }
    }
  }, [selectedTemplateId, templates]);

  const loadTemplates = async () => {
    try {
      console.log('Loading templates from:', `${API_BASE_URL}/api/dispatch/templates`);
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('No auth token found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/dispatch/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.error('Template load failed with status:', response.status);
        if (response.status === 401) {
          console.error('Authentication failed. Token may be expired.');
          // 401인 경우 로그인 페이지로 리다이렉트할 수도 있습니다
        }
        return; // JSON 파싱 시도하지 않고 종료
      }

      const text = await response.text();
      if (!text) {
        console.error('Empty response body');
        return;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse JSON:', text);
        return;
      }

      console.log('Template response:', data);

      // 모든 템플릿의 선택지를 맵에 저장
      const newChoicesMap = new Map<string, string>();
      if (data.success && Array.isArray(data.data)) {
        data.data.forEach((template: DispatchTemplate) => {
          template.choices?.forEach((choice: any) => {
            newChoicesMap.set(choice.key, choice.label);
          });
        });
      }
      setChoicesMap(newChoicesMap);
      if (data.success) {
        setTemplates(data.data);
      } else {
        console.error('Template response indicates failure:', data);
      }
    } catch (error) {
      console.error('템플릿 로드 실패:', error);
    }
  };

  const loadDispatchHistory = () => {
    try {
      const savedHistory = localStorage.getItem('dispatchHistory');
      if (savedHistory) {
        setDispatchHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error('발송 내역 로드 실패:', error);
    }
  };

  const loadMessageTemplates = () => {
    try {
      const savedTemplates = localStorage.getItem('dispatchMessageTemplates');
      if (savedTemplates) {
        setMessageTemplates(JSON.parse(savedTemplates));
      }
    } catch (error) {
      console.error('메시지 템플릿 로드 실패:', error);
    }
  };

  const saveMessageTemplate = (message: string, question: string, choices?: Array<{ key: string; label: string; status: string }>) => {
    const exists = messageTemplates.some(t => t.message === message && t.question === question);
    if (!exists && message && question) {
      const newTemplate: MessageTemplate = {
        id: Date.now().toString(),
        message,
        question,
        choices: choices || selectedTemplate?.choices,
        timestamp: new Date().toISOString()
      };
      const updated = [newTemplate, ...messageTemplates].slice(0, 10);
      setMessageTemplates(updated);
      localStorage.setItem('dispatchMessageTemplates', JSON.stringify(updated));
    }
  };

  const saveToHistory = (phoneNumbers: string, templateName: string) => {
    try {
      const newHistory: DispatchHistory = {
        id: Date.now().toString(),
        phoneNumbers,
        timestamp: new Date().toISOString(),
        count: phoneNumbers.split('|').filter(p => p.trim()).length,
        templateName
      };
      const updated = [newHistory, ...dispatchHistory].slice(0, 20);
      setDispatchHistory(updated);
      localStorage.setItem('dispatchHistory', JSON.stringify(updated));
    } catch (error) {
      console.error('발송 내역 저장 실패:', error);
    }
  };

  const handleHistorySelect = (historyId: string) => {
    setSelectedHistory(prev => {
      if (prev.includes(historyId)) {
        return prev.filter(id => id !== historyId);
      } else {
        return [...prev, historyId];
      }
    });
  };

  const addSelectedToForm = () => {
    const selected = dispatchHistory.filter(h => selectedHistory.includes(h.id));
    if (selected.length === 0) {
      alert('선택된 내역이 없습니다.');
      return;
    }

    const allPhones = selected.flatMap(h => h.phoneNumbers.split('|').filter(p => p.trim()));
    const currentPhones = phoneNumbers.split('|').filter(p => p.trim());
    const combined = [...currentPhones, ...allPhones];
    setPhoneNumbers(combined.join('|'));

    setShowHistoryModal(false);
    setSelectedHistory([]);
    alert(`${allPhones.length}개의 전화번호가 추가되었습니다.`);
  };

  const openFullHistoryModal = async () => {
    setShowFullHistoryModal(true);
    await loadFullHistory();
  };

  const loadFullHistory = async () => {
    setLoadingFullHistory(true);
    try {
      // 필터 파라미터 구성
      const params = new URLSearchParams();
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      if (filterStatus) params.append('status', filterStatus);
      if (filterPhoneNumber) params.append('phoneNumber', filterPhoneNumber);
      // 배차 오토콜 페이지에서는 TTS_RS이면서 배차 오토콜만 표시
      params.append('serviceType', 'TTS_RS');
      params.append('isDispatch', 'true');

      const response = await fetch(`${API_BASE_URL}/api/call-history?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('발신 내역을 불러올 수 없습니다.');
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // 발신시각 역순 정렬 (최신순)
        const sortedData = data.sort((a: FullDispatchHistory, b: FullDispatchHistory) =>
          new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
        );
        setFullDispatchHistory(sortedData);
      } else {
        setFullDispatchHistory([]);
      }
    } catch (error: any) {
      console.error('Dispatch history load error:', error);
      alert(`발신 내역 조회 오류: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setLoadingFullHistory(false);
    }
  };

  const downloadCSV = () => {
    // 필터 파라미터 구성
    const params = new URLSearchParams();
    if (filterStartDate) params.append('startDate', filterStartDate);
    if (filterEndDate) params.append('endDate', filterEndDate);
    if (filterStatus) params.append('status', filterStatus);
    if (filterPhoneNumber) params.append('phoneNumber', filterPhoneNumber);
    // 배차 오토콜 페이지에서는 TTS_RS이면서 배차 오토콜만 다운로드
    params.append('serviceType', 'TTS_RS');
    params.append('isDispatch', 'true');

    const url = `${API_BASE_URL}/api/call-history/export-csv?${params.toString()}`;
    const token = localStorage.getItem('token');

    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dispatch_history_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(error => {
        console.error('CSV download error:', error);
        alert('CSV 다운로드 중 오류가 발생했습니다.');
      });
  };

  const applyFilters = () => {
    loadFullHistory();
  };

  const resetFilters = () => {
    setFilterStatus('');
    setFilterServiceType('TTS_RS');
    setFilterPhoneNumber('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const getPhoneCount = () => {
    return phoneNumbers.split('|').filter(p => p.trim()).length;
  };

  const getTotalCost = () => {
    return getPhoneCount() * COST_PER_CALL;
  };

  const detectDelimiter = (content: string): string => {
    const delimiters = [',', '\t', '|', ';'];
    const lines = content.split(/[\r\n]+/).filter(line => line.trim()).slice(0, 5);

    let maxCount = 0;
    let bestDelimiter = ',';

    delimiters.forEach(delimiter => {
      const counts = lines.map(line => line.split(delimiter).length);
      const avgCount = counts.reduce((a, b) => a + b, 0) / counts.length;
      if (avgCount > maxCount) {
        maxCount = avgCount;
        bestDelimiter = delimiter;
      }
    });

    return maxCount > 1 ? bestDelimiter : ',';
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (isExcel) {
      // 엑셀 파일 처리
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = event.target?.result;
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];

          if (jsonData.length === 0) {
            alert('엑셀 파일이 비어있습니다.');
            return;
          }

          const headers = jsonData[0].map((h: any) => String(h || ''));
          const rows = jsonData.slice(1).filter((row: any[]) => row.some((cell: any) => cell));

          setFileHeaders(headers);
          setFileRows(rows);
          setShowColumnModal(true);
        } catch (error) {
          console.error('엑셀 파일 처리 오류:', error);
          alert('엑셀 파일 처리 중 오류가 발생했습니다.');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // CSV/TXT 파일 처리
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const content = event.target?.result as string;
          const lines = content.split(/[\r\n]+/).filter(line => line.trim());

          if (lines.length === 0) {
            alert('파일이 비어있습니다.');
            return;
          }

          const delimiter = detectDelimiter(content);
          const rows = lines.map(line => line.split(delimiter).map(cell => cell.trim()));

          const hasHeader = isNaN(Number(rows[0][0]?.replace(/\D/g, '')));
          const headers = hasHeader ? rows[0] : rows[0].map((_, i) => `컬럼 ${i + 1}`);
          const dataRows = hasHeader ? rows.slice(1) : rows;

          setFileHeaders(headers);
          setFileRows(dataRows);
          setShowColumnModal(true);
        } catch (error) {
          console.error('파일 처리 오류:', error);
          alert('파일 처리 중 오류가 발생했습니다.');
        }
      };
      reader.readAsText(file);
    }

    e.target.value = '';
  };

  const convertExcelTimeToString = (value: any): string => {
    // 이미 시간 형식 문자열인 경우 (예: "08:00", "8시")
    if (typeof value === 'string') {
      return value.trim();
    }

    // 숫자인 경우 Excel 시간 형식으로 간주 (0.333... = 8시간)
    if (typeof value === 'number') {
      // Excel에서 시간은 하루를 1로 표현 (0.5 = 12시간)
      const totalMinutes = Math.round(value * 24 * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      // HH:MM 형식으로 반환
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    return String(value || '').trim();
  };

  const confirmColumnSelection = () => {
    if (selectedColumn === -1) {
      alert('전화번호 컬럼을 선택해주세요.');
      return;
    }

    const phones = fileRows
      .map(row => String(row[selectedColumn] || '').trim())
      .filter(phone => phone);

    const times = selectedTimeColumn !== -1
      ? fileRows
          .map(row => convertExcelTimeToString(row[selectedTimeColumn]))
          .filter(time => time)
      : [];

    const currentPhones = phoneNumbers.split('|').filter(p => p.trim());
    const newPhones = [...currentPhones, ...phones];
    setPhoneNumbers(newPhones.join('|'));

    if (times.length > 0) {
      const currentTimes = scheduledTimes.split('|').filter(t => t.trim());
      const newTimes = [...currentTimes, ...times];
      setScheduledTimes(newTimes.join('|'));
      setUseScheduling(true);
    }

    setShowColumnModal(false);
    setSelectedColumn(-1);
    setSelectedTimeColumn(-1);
    setFileHeaders([]);
    setFileRows([]);

    alert(`${phones.length}개의 전화번호가 추가되었습니다.`);
  };

  const sendDispatch = async () => {
    if (!selectedTemplateId) {
      alert('템플릿을 선택해주세요.');
      return;
    }

    if (!phoneNumbers) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    if ((user?.creditBalance || 0) < getTotalCost()) {
      alert('크레딧이 부족합니다. 크레딧을 충전해주세요.');
      return;
    }

    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
    const templateName = selectedTemplate?.name || '알 수 없음';

    // 실시간 발송 현황 초기화
    const phones = phoneNumbers.split('|').filter(p => p.trim());
    const results: DispatchResult[] = phones.map(phone => ({
      id: `${Date.now()}-${phone}`,
      phoneNumber: phone,
      status: 'pending',
      templateName: templateName,
      timestamp: new Date().toISOString()
    }));
    setDispatchResults(results);

    setLoading(true);
    try {
      // 수정된 메시지가 있으면 전달
      const fullCustomMessage = customMessage && customQuestion
        ? `${customMessage} ${customQuestion}`
        : null;

      const response = await fetch(`${API_BASE_URL}/api/dispatch/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          templateId: selectedTemplateId,
          phoneNumbers: phoneNumbers,
          scheduledTimes: useScheduling ? scheduledTimes : null,
          customMessage: fullCustomMessage,
          eventFlag: 'Y'
        })
      });

      const data = await response.json();
      if (data.success) {
        // 성공 시 결과 업데이트
        setDispatchResults(results.map(r => ({ ...r, status: 'success' })));

        // 내역 저장
        saveToHistory(phoneNumbers, templateName);

        // 메시지 템플릿 저장
        if (customMessage && customQuestion) {
          saveMessageTemplate(customMessage, customQuestion, selectedTemplate?.choices);
        }

        alert(data.data.message || '배차 오토콜이 발송되었습니다.');
        setPhoneNumbers('');
        setScheduledTimes('');
        setUseScheduling(false);
      } else {
        // 실패 시 결과 업데이트
        setDispatchResults(results.map(r => ({ ...r, status: 'failed' })));
        const errorMessage = getErrorMessage(data.message);
        alert('발송 실패: ' + errorMessage);
      }
    } catch (error: any) {
      console.error('발송 실패:', error);
      setDispatchResults(results.map(r => ({ ...r, status: 'failed' })));
      const errorMessage = getErrorMessage(error.message);
      alert('발송 중 오류가 발생했습니다: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dispatch/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      if (data.success) {
        setReportData(data.data);
        setAlerts(data.data.alerts || []);
      }
    } catch (error) {
      console.error('리포트 조회 실패:', error);
      alert('리포트 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'report') {
      loadReport();
    }
  }, [activeTab]);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  return (
    <div className="dispatch-call-page">
      <section className="dispatch-call-content section">
        <div className="container-narrow">
          <div className="dispatch-call-card card">
            <h2 className="card-title">배차 오토콜</h2>

            <div className="dispatch-tabs">
              <button
                className={`tab-button ${activeTab === 'send' ? 'active' : ''}`}
                onClick={() => setActiveTab('send')}
              >
                배차 발송
              </button>
              <button
                className={`tab-button ${activeTab === 'report' ? 'active' : ''}`}
                onClick={() => setActiveTab('report')}
              >
                상세 리포트
              </button>
            </div>

            {activeTab === 'send' && (
              <div className="dispatch-send">
                {/* 메인 폼 */}
                <div className="dispatch-send-main">
                  <div className="card">
                    <h3 className="card-title">발신 정보</h3>

                    <div className="form-group">
                      <label className="form-label">템플릿 선택 *</label>
                      <select
                        className="form-select"
                        value={selectedTemplateId || ''}
                        onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                      >
                        <option value="">템플릿을 선택하세요</option>
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>


                    <div className="form-group">
                      <label className="form-label">
                        전화번호 *
                        <span className="form-hint">
                          (구분자: | 예: 010-1111-1111|010-2222-2222)
                        </span>
                      </label>
                      <div className="phone-input-group">
                        <textarea
                          className="form-textarea phone-textarea"
                          placeholder="010-1234-5678|010-2345-6789|..."
                          value={phoneNumbers}
                          onChange={(e) => setPhoneNumbers(e.target.value)}
                          rows={4}
                        />
                        <div className="phone-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setShowHistoryModal(true)}
                          >
                            이전 내역에서 선택
                          </button>
                          <label className="btn btn-secondary btn-sm file-upload-btn">
                            파일 업로드 (CSV/Excel)
                            <input
                              type="file"
                              accept=".csv,.txt,.xlsx,.xls"
                              onChange={handleFileUpload}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                        <div className="phone-count">
                          총 {getPhoneCount()}개의 번호
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={useScheduling}
                          onChange={(e) => setUseScheduling(e.target.checked)}
                        />
                        <span>예약 발송 사용 (시간 지정)</span>
                      </label>
                    </div>

                    {useScheduling && (
                      <div className="form-group">
                        <label className="form-label">
                          예약 시간 (선택)
                          <span className="form-hint">
                            (시간만 입력 시 자동 익일 처리, 날짜 지정도 가능)
                          </span>
                        </label>
                        <textarea
                          className="form-textarea"
                          placeholder="08:00|14:30|20:00 또는 2025-01-22 08:00|2025-01-22 14:30"
                          value={scheduledTimes}
                          onChange={(e) => setScheduledTimes(e.target.value)}
                          rows={4}
                        />
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px' }}>
                          <div><strong>시간만 입력:</strong> 08:00|14:30 (과거 시간이면 자동 익일 발송)</div>
                          <div><strong>날짜+시간:</strong> 2025-01-22 08:00|2025-01-23 14:30 (특정 날짜 지정)</div>
                          <div style={{ marginTop: '4px', color: '#9ca3af' }}>※ 전화번호와 같은 순서로 입력</div>
                        </div>
                      </div>
                    )}

                    {/* 메시지 수정 */}
                    <div className="form-group">
                      <label className="form-label">
                        멘트 (메시지) *
                      </label>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span className="form-hint">템플릿 선택 시 자동으로 채워집니다</span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowMessageModal(true)}
                          style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                        >
                          최근 메시지
                        </button>
                      </div>
                      <textarea
                        className="form-textarea"
                        placeholder="템플릿을 선택하면 내용이 표시됩니다..."
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">질문 *</label>
                      <textarea
                        className="form-textarea"
                        placeholder="템플릿을 선택하면 내용이 표시됩니다..."
                        value={customQuestion}
                        onChange={(e) => setCustomQuestion(e.target.value)}
                        rows={2}
                      />
                    </div>

                    {selectedTemplate && selectedTemplate.choices && selectedTemplate.choices.length > 0 && (
                      <div className="form-group">
                        <label className="form-label">선택지</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {selectedTemplate.choices.map((choice, idx) => (
                            <span
                              key={idx}
                              style={{
                                padding: '0.4rem 0.8rem',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                fontWeight: '500',
                                backgroundColor: choice.status === '완료' ? '#d1fae5' : '#fef3c7',
                                color: choice.status === '완료' ? '#059669' : '#d97706',
                                border: `1px solid ${choice.status === '완료' ? '#10b981' : '#fbbf24'}`
                              }}
                            >
                              {choice.key}번: {choice.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-lg"
                        onClick={sendDispatch}
                        disabled={loading || !selectedTemplateId || getPhoneCount() === 0 || (user?.creditBalance || 0) < getTotalCost()}
                      >
                        {loading ? '발송 중...' : `배차 발송 (${getPhoneCount()}건 / ${getTotalCost().toLocaleString()}원)`}
                      </button>
                    </div>
                  </div>
                </div>



                {/* 사이드바 - 크레딧 정보 및 발송 현황 */}
                <div className="dispatch-send-sidebar">
                  {/* 전체 발신 내역 버튼 */}
                  <button
                    type="button"
                    onClick={openFullHistoryModal}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      marginBottom: '1rem',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#374151',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>📋</span>
                    <span>전체 발신 내역</span>
                  </button>

                  {/* 실시간 발송 현황 */}
                  {dispatchResults.length > 0 && (
                    <div className="card" style={{ marginTop: '1rem' }}>
                      <h3 className="card-title">실시간 발송 현황</h3>
                      <div className="dispatch-results">
                        <div className="results-list">
                          {dispatchResults.slice(0, 5).map(result => (
                            <div key={result.id} className={`result-item status-${result.status}`}>
                              <div className="result-phone">{result.phoneNumber}</div>
                              <div className="result-status">
                                {result.status === 'pending' && '대기중'}
                                {result.status === 'calling' && '발송중...'}
                                {result.status === 'success' && '✓ 성공'}
                                {result.status === 'failed' && '✗ 실패'}
                              </div>
                            </div>
                          ))}
                        </div>
                        {dispatchResults.length > 5 && (
                          <div style={{ textAlign: 'center', padding: '0.5rem', color: '#6b7280', fontSize: '0.85rem' }}>
                            외 {dispatchResults.length - 5}건
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="card" style={{ marginTop: '1rem' }}>
                    <h3 className="card-title">크레딧 및 비용 정보</h3>
                    <div className="credit-info-box">
                      <div className="credit-info-item">
                        <span className="info-label">보유 크레딧</span>
                        <span className="info-value">{(user?.creditBalance || 0).toLocaleString()}원</span>
                      </div>
                      <div className="credit-info-item">
                        <span className="info-label">발신 건수</span>
                        <span className="info-value highlight">{getPhoneCount()}건</span>
                      </div>
                      <div className="credit-info-item">
                        <span className="info-label">예상 비용</span>
                        <span className="info-value cost">{getTotalCost().toLocaleString()}원</span>
                      </div>
                      <div className="credit-info-item">
                        <span className="info-label">발신 후 잔액</span>
                        <span className={`info-value ${(user?.creditBalance || 0) - getTotalCost() < 0 ? 'warning' : ''}`}>
                          {Math.max(0, (user?.creditBalance || 0) - getTotalCost()).toLocaleString()}원
                        </span>
                      </div>
                    </div>
                    <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
                      ※ 크레딧은 발신 성공 시에만 차감되며, 발신 실패 시에는 차감되지 않습니다.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'report' && (
              <div className="dispatch-report">
                {loading ? (
                  <div className="empty-state">리포트를 불러오는 중...</div>
                ) : reportData ? (
                  <>
                    {alerts.length > 0 && (
                      <div className="alerts-section">
                        <h3>⚠️ 예외상황 ({alerts.length}건)</h3>
                        <div className="alerts-list">
                          {alerts.map((alert, idx) => (
                            <div key={idx} className={`alert-item ${alert.status}`}>
                              <div className="alert-header">
                                <span className="alert-phone">{alert.phoneNumber}</span>
                                <span className={`alert-badge ${alert.status}`}>
                                  {alert.status === 'warning' ? '⚠️ 주의' : '🚨 긴급'}
                                </span>
                              </div>
                              <div className="alert-body">
                                <p><strong>응답:</strong> {alert.responseLabel}</p>
                                <p><strong>시간:</strong> {new Date(alert.respondedAt).toLocaleString('ko-KR')}</p>
                                {alert.scheduledTime && (
                                  <p><strong>예약시간:</strong> {new Date(alert.scheduledTime).toLocaleString('ko-KR')}</p>
                                )}
                              </div>
                              <div className="alert-actions">
                                <button
                                  onClick={async () => {
                                    try {
                                      const hostname = window.location.hostname;
                                      const API_BASE_URL = process.env.REACT_APP_API_URL ||
                                        (hostname === 'localhost' ? 'http://localhost:5111' :
                                         hostname === 'autocall.seoul-soft.com' ? 'https://autocall.seoul-soft.com' :
                                         `http://${hostname}:5111`);

                                      const response = await fetch(`${API_BASE_URL}/api/dispatch/alerts/${alert.callItemId}/complete`, {
                                        method: 'POST',
                                        headers: {
                                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                                        }
                                      });

                                      if (response.ok) {
                                        // 조치완료 성공 - alert 목록에서 제거
                                        setAlerts(prevAlerts => prevAlerts.filter((_, i) => i !== idx));
                                      } else {
                                        window.alert('조치완료 처리 중 오류가 발생했습니다.');
                                      }
                                    } catch (error) {
                                      console.error('조치완료 처리 실패:', error);
                                      window.alert('조치완료 처리 중 오류가 발생했습니다.');
                                    }
                                  }}
                                  className="btn-complete"
                                >
                                  조치완료
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="report-summary">
                      <div className="summary-card">
                        <div className="summary-label">총 발송</div>
                        <div className="summary-value">{reportData.totalCalls}건</div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">완료</div>
                        <div className="summary-value success">{reportData.completedCalls}건</div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">예약</div>
                        <div className="summary-value">{reportData.scheduledCalls}건</div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">실패</div>
                        <div className="summary-value failed">{reportData.failedCalls}건</div>
                      </div>
                    </div>

                    <div className="response-distribution">
                      <h3>응답 현황</h3>
                      {Object.entries(reportData.responseDistribution || {}).map(([key, count]: [string, any]) => (
                        <div key={key} className="distribution-item">
                          <span className="distribution-label">{key}</span>
                          <span className="distribution-count">{count}건</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="empty-state">리포트 데이터가 없습니다.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 컬럼 선택 모달 */}
      {showColumnModal && (
        <div className="modal-overlay" onClick={() => setShowColumnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>전화번호 컬럼 선택</h2>
              <button className="modal-close" onClick={() => setShowColumnModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                전화번호가 포함된 컬럼을 선택해주세요.
              </p>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                  전화번호 컬럼 *
                </label>
                {fileHeaders.map((header, index) => (
                  <div key={index} style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="phoneColumn"
                        value={index}
                        checked={selectedColumn === index}
                        onChange={() => setSelectedColumn(index)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <span>{header || `컬럼 ${index + 1}`}</span>
                      {fileRows[0] && fileRows[0][index] && (
                        <span style={{ marginLeft: '0.5rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                          (예: {String(fileRows[0][index])})
                        </span>
                      )}
                    </label>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                  예약 시간 컬럼 (선택)
                </label>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="timeColumn"
                      value={-1}
                      checked={selectedTimeColumn === -1}
                      onChange={() => setSelectedTimeColumn(-1)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <span>사용 안함</span>
                  </label>
                </div>
                {fileHeaders.map((header, index) => (
                  <div key={index} style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="timeColumn"
                        value={index}
                        checked={selectedTimeColumn === index}
                        onChange={() => setSelectedTimeColumn(index)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <span>{header || `컬럼 ${index + 1}`}</span>
                      {fileRows[0] && fileRows[0][index] && (
                        <span style={{ marginLeft: '0.5rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                          (예: {convertExcelTimeToString(fileRows[0][index])})
                        </span>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowColumnModal(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmColumnSelection}
                disabled={selectedColumn === -1}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이전 내역 선택 모달 */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>이전 발송 내역에서 선택</h2>
              <button className="modal-close" onClick={() => setShowHistoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {dispatchHistory.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  저장된 발송 내역이 없습니다.
                </div>
              ) : (
                <div className="history-list">
                  {dispatchHistory.map((history) => (
                    <div
                      key={history.id}
                      className={`history-item ${selectedHistory.includes(history.id) ? 'selected' : ''}`}
                    >
                      <div className="history-header">
                        <span className="history-template">{history.templateName}</span>
                        <span className="history-count">{history.count}건</span>
                      </div>
                      <div className="history-timestamp">
                        {new Date(history.timestamp).toLocaleString('ko-KR')}
                      </div>
                      <div className="history-phones">
                        {history.phoneNumbers.split('|').slice(0, 3).join(', ')}
                        {history.count > 3 && ` 외 ${history.count - 3}개`}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleHistorySelect(history.id)}
                        >
                          {selectedHistory.includes(history.id) ? '선택 해제' : '선택'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            const updated = dispatchHistory.filter(h => h.id !== history.id);
                            setDispatchHistory(updated);
                            localStorage.setItem('dispatchHistory', JSON.stringify(updated));
                            // 선택된 항목에서도 제거
                            setSelectedHistory(prev => prev.filter(id => id !== history.id));
                          }}
                          style={{
                            padding: '4px 12px',
                            fontSize: '0.875rem',
                            color: '#ef4444',
                            backgroundColor: 'transparent',
                            border: '1px solid #ef4444',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowHistoryModal(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={addSelectedToForm}
                disabled={selectedHistory.length === 0}
              >
                선택한 내역 추가 ({selectedHistory.length}개)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 전체 발신 내역 모달 */}
      {showFullHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowFullHistoryModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 전체 발신 내역 (배차 오토콜)</h2>
              <button className="modal-close" onClick={() => setShowFullHistoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* 필터 섹션 */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>시작일</label>
                    <input
                      type="date"
                      value={filterStartDate}
                      onChange={(e) => setFilterStartDate(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>종료일</label>
                    <input
                      type="date"
                      value={filterEndDate}
                      onChange={(e) => setFilterEndDate(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>상태</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    >
                      <option value="">전체</option>
                      <option value="success">성공</option>
                      <option value="failed">실패</option>
                      <option value="pending">대기중</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>전화번호</label>
                    <input
                      type="text"
                      value={filterPhoneNumber}
                      onChange={(e) => setFilterPhoneNumber(e.target.value)}
                      placeholder="010-1234-5678"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={resetFilters}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    초기화
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    조회
                  </button>
                  <button
                    type="button"
                    onClick={downloadCSV}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    📥 CSV 다운로드
                  </button>
                </div>
              </div>

              {loadingFullHistory ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  불러오는 중...
                </div>
              ) : fullDispatchHistory.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  발신 내역이 없습니다.
                </div>
              ) : (
                <div className="full-history-table-wrapper">
                  <table className="full-history-table">
                    <thead>
                      <tr>
                        <th>전화번호</th>
                        <th>메시지 내용</th>
                        <th>선택지</th>
                        <th>응답</th>
                        <th>상태</th>
                        <th>비용</th>
                        <th>발신시각</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fullDispatchHistory.map((item) => {
                        // 템플릿 ID 추출
                        const templateId = item.surveyTitle?.match(/템플릿 (\d+)/)?.[1];
                        const template = templateId ? templates.find(t => t.id.toString() === templateId) : null;

                        return (
                          <tr key={item.id}>
                            <td>{item.phoneNumber}</td>
                            <td style={{ minWidth: '150px', maxWidth: '300px' }}>
                              <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {item.message || '-'}
                              </div>
                            </td>
                            <td style={{ minWidth: '200px' }}>
                              {template && template.choices && template.choices.length > 0 ? (
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  {template.choices.map((choice, idx) => (
                                    <div key={idx} style={{ marginBottom: '0.2rem' }}>
                                      {choice.key}번: {choice.label}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                            <td>
                              {item.surveyAnswers && item.surveyAnswers.length > 0 ? (
                                <div style={{ fontSize: '0.85rem' }}>
                                  {item.surveyAnswers.map((answer, idx) => {
                                    const answerKey = answer.answer;
                                    // 응답 키가 비어있거나 유효하지 않으면 미응답으로 처리
                                    if (!answerKey || answerKey.trim() === '') {
                                      return (
                                        <span key={idx} style={{ color: '#9ca3af', fontSize: '0.85rem' }}>미응답</span>
                                      );
                                    }
                                    const answerLabel = template?.choices?.find(c => c.key === answerKey)?.label || '';
                                    return (
                                      <div key={idx} style={{ marginBottom: '0.25rem' }}>
                                        <strong style={{ color: '#1f2937' }}>{answerKey}번</strong>
                                        {answerLabel && (
                                          <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                                            {answerLabel}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>미응답</span>
                              )}
                            </td>
                            <td>
                              <span className={`status-badge ${item.status}`}>
                                {item.status === 'success' && '✅ 성공'}
                                {item.status === 'failed' && '❌ 실패'}
                                {item.status === 'pending' && '⏳ 대기'}
                              </span>
                            </td>
                            <td>{item.cost.toLocaleString()}원</td>
                            <td style={{ minWidth: '140px' }}>{new Date(item.sentAt).toLocaleString('ko-KR')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
                    총 {fullDispatchHistory.length}건
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowFullHistoryModal(false)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 최근 메시지 선택 모달 */}
      {showMessageModal && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>최근 발송 메시지</h2>
              <button className="modal-close" onClick={() => setShowMessageModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {messageTemplates.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  저장된 메시지가 없습니다.
                </div>
              ) : (
                <div className="history-list">
                  {messageTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="history-item"
                      onClick={() => {
                        setCustomMessage(template.message);
                        setCustomQuestion(template.question);

                        // 저장된 선택지가 있으면 해당 템플릿 찾기
                        if (template.choices && template.choices.length > 0) {
                          const matchingTemplate = templates.find(t =>
                            JSON.stringify(t.choices) === JSON.stringify(template.choices)
                          );
                          if (matchingTemplate) {
                            setSelectedTemplateId(matchingTemplate.id);
                          }
                        }

                        setShowMessageModal(false);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong style={{ fontSize: '0.9rem', color: '#374151' }}>멘트:</strong>
                        <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
                          {template.message.length > 50 ? `${template.message.substring(0, 50)}...` : template.message}
                        </p>
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong style={{ fontSize: '0.9rem', color: '#374151' }}>질문:</strong>
                        <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
                          {template.question.length > 50 ? `${template.question.substring(0, 50)}...` : template.question}
                        </p>
                      </div>
                      {template.choices && template.choices.length > 0 && (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ fontSize: '0.9rem', color: '#374151' }}>선택지:</strong>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.25rem' }}>
                            {template.choices.map((choice, idx) => (
                              <span
                                key={idx}
                                style={{
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  backgroundColor: '#f3f4f6',
                                  color: '#6b7280',
                                  border: '1px solid #d1d5db'
                                }}
                              >
                                {choice.key}:{choice.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="history-timestamp">
                        {new Date(template.timestamp).toLocaleString('ko-KR')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowMessageModal(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchCall;
